import {
  Award_Per_Counting_Strategy,
  EarningMechanismConfigurationInput,
  EarningMechanismProductDiscountInput,
  EarningMechanismTransactionDiscountInput,
  EarningMechanismVarietyDiscountInput,
  OfferFragment,
} from '@occc/graphql';
import { dollarsToCents } from '@occc-app/lib/currency';
import { DirtyFieldsType, Operation } from '@occc-app/offerHelpers/types';
import { isFieldDirty } from '@occc-app/offerHelpers/utils';
import { makeDeleteOperation } from '@occc-app/offerHelpers/operations/makeDeleteOperation';
import { makeEarningMechanismCreateOperation } from '@occc-app/offerHelpers/operations/earningMechanism';
import { earningMechanismInputCreator } from '@occc-app/offerHelpers/earningMechanismInputCreator';

import {
  BuyAndGetFormValues,
  FuelRollbackFormValues,
  ProductDiscountAwardType,
  ProductDiscountFormValues,
  PromotionConfigureFormValues,
  TransactionDiscountAwardType,
  TransactionDiscountFormValues,
  VarietyDiscountAwardType,
  VarietyDiscountFormValues,
  PromotionType,
} from '../../../types';

type EarningMechanismCreateInput = Pick<
  EarningMechanismConfigurationInput,
  'transactionDiscount' | 'productDiscount' | 'varietyDiscount'
>;

type GetEarningMechanismCreateInput = (
  values: PromotionConfigureFormValues,
) => EarningMechanismCreateInput;

type PrepareTransactionDiscountCreateInput = (
  values: TransactionDiscountFormValues,
  isMinimumPurchaseConstraintsEnabled?: boolean,
) => {
  transactionDiscount: EarningMechanismTransactionDiscountInput;
};

const prepareTransactionDiscountCreateInput: PrepareTransactionDiscountCreateInput = (
  values,
  isMinimumPurchaseConstraintsEnabled = false,
) => {
  const { ineligibleProductGroups, purchaseAmountThreshold, awardType } = values;
  const moneyDiscount =
    awardType === TransactionDiscountAwardType.MoneyDiscount ? values.moneyDiscount : undefined;
  const percentDiscount =
    awardType === TransactionDiscountAwardType.PercentDiscount ? values.percentDiscount : undefined;
  const moneyDiscountCents = dollarsToCents(moneyDiscount ?? 0);
  const purchaseAmountThresholdCents = dollarsToCents(purchaseAmountThreshold ?? 0);
  const isPercentDiscount = awardType === TransactionDiscountAwardType.PercentDiscount;
  const isMoneyDiscount = awardType === TransactionDiscountAwardType.MoneyDiscount;
  const awardInput = isPercentDiscount
    ? { awardTransactionPercentDiscount: { percentDiscount: percentDiscount! } }
    : { awardTransactionMoneyDiscount: { moneyDiscount: moneyDiscountCents! } };

  const ineligibleProductGroupIds = ineligibleProductGroups?.length
    ? ineligibleProductGroups.map((item) => item.id)
    : [];

  const allowMultipleFulfillmentsPerProductGroupThreshold =
    isMinimumPurchaseConstraintsEnabled &&
    isMoneyDiscount &&
    !!values.allowMultipleFulfillmentsPerProductGroupThreshold;

  return earningMechanismInputCreator.createTransactionDiscount(
    purchaseAmountThresholdCents,
    ineligibleProductGroupIds,
    awardInput,
    allowMultipleFulfillmentsPerProductGroupThreshold || undefined,
  );
};

type PrepareProductDiscountCreateInput = (values: ProductDiscountFormValues) => {
  productDiscount: EarningMechanismProductDiscountInput;
};

const prepareProductDiscountCreateInput: PrepareProductDiscountCreateInput = (values) => {
  const { eligibleProductGroups, purchaseThreshold, awardType, moneyAmount } = values;
  const moneyAmountCents = dollarsToCents(moneyAmount ?? 0);
  const awardInputMap = {
    [ProductDiscountAwardType.Free]: {
      awardPriceOverride: {
        maximumCost: 0,
      },
    },
    [ProductDiscountAwardType.Discount]: {
      awardMoneyDiscount: {
        moneyAmount: moneyAmountCents,
        awardPer:
          values.awardType === ProductDiscountAwardType.Discount
            ? values.countProductsBy ?? Award_Per_Counting_Strategy.IndividualPurchase
            : Award_Per_Counting_Strategy.IndividualPurchase,
      },
    },
    [ProductDiscountAwardType.PriceOverride]: {
      awardPriceOverride: {
        maximumCost: moneyAmountCents,
      },
    },
  };

  const eligibleProductGroupIds = eligibleProductGroups?.map((item) => item.id);

  return earningMechanismInputCreator.createProductDiscount(
    purchaseThreshold,
    eligibleProductGroupIds,
    awardInputMap[awardType],
  );
};

const prepareFuelRollbackCreateInput = (
  values: FuelRollbackFormValues,
): { productDiscount: EarningMechanismProductDiscountInput } => {
  return {
    productDiscount: {
      purchaseThreshold: 1,
      eligibleProductGroupIds: values.eligibleProductGroups.map((group) => group.id),
      awardMoneyDiscount: {
        moneyAmount: dollarsToCents(values.moneyAmount ?? 0),
        awardPer: Award_Per_Counting_Strategy.IndividualPurchase,
      },
    },
  };
};

type PrepareBuyAndGoCreateInput = (values: BuyAndGetFormValues) => {
  productDiscount: EarningMechanismProductDiscountInput;
};

const prepareBuyAndGetCreateInput: PrepareBuyAndGoCreateInput = (values) => {
  const { eligibleProductGroups, discountThreshold, purchaseThreshold } = values;

  const eligibleProductGroupIds = eligibleProductGroups?.map((item) => item.id);

  const awardInput: { awardBuyAndGet: EarningMechanismProductDiscountInput['awardBuyAndGet'] } = {
    awardBuyAndGet: {
      discountThreshold: discountThreshold,
      percentDiscount: 100,
    },
  };

  return earningMechanismInputCreator.createProductDiscount(
    purchaseThreshold,
    eligibleProductGroupIds,
    awardInput,
  );
};

const prepareVarietyDiscountCreateInput = (
  values: VarietyDiscountFormValues,
): { varietyDiscount: EarningMechanismVarietyDiscountInput } => {
  const { awardType, purchases: purchasesFormValues } = values;

  const purchases: EarningMechanismVarietyDiscountInput['purchases'] = [];

  purchasesFormValues?.forEach((purchase) => {
    purchases.push({
      purchaseThreshold: purchase.purchaseThreshold,
      eligibleProductGroupIds: purchase.eligibleProductGroups.map((group) => group.id),
    });
  });

  if (awardType === VarietyDiscountAwardType.TransactionDiscount) {
    const { moneyDiscount } = values.transactionDiscount;
    const moneyDiscountCents = dollarsToCents(moneyDiscount);

    return earningMechanismInputCreator.createVarietyDiscount(purchases, {
      awardTransactionMoneyDiscount: {
        moneyDiscount: moneyDiscountCents,
      },
    });
  }

  if (awardType === VarietyDiscountAwardType.ProductDiscount) {
    const {
      eligibleProductGroups,
      purchaseThreshold,
      awardType: productAwardType,
    } = values.productDiscount;
    const percentDiscount =
      productAwardType === ProductDiscountAwardType.Discount
        ? values.productDiscount.percentDiscount
        : undefined;

    return earningMechanismInputCreator.createVarietyDiscount(purchases, {
      awardProductDiscount: {
        purchaseThreshold,
        percentDiscount:
          productAwardType === ProductDiscountAwardType.Free ? 100 : percentDiscount ?? 0,
        eligibleProductGroupIds: eligibleProductGroups.map((group) => group.id),
      },
    });
  }

  if (awardType === VarietyDiscountAwardType.PriceOverride) {
    const { maximumCost } = values.priceOverride;
    const maximumCostCents = dollarsToCents(maximumCost);

    return earningMechanismInputCreator.createVarietyDiscount(purchases, {
      awardPriceOverride: {
        maximumCost: maximumCostCents,
      },
    });
  }

  throw new Error('award type is not selected');
};

export const getEarningMechanismCreateInput: GetEarningMechanismCreateInput = (values) => {
  if (values.offerType === PromotionType.TransactionDiscount) {
    return prepareTransactionDiscountCreateInput(
      values.transactionDiscount,
      values.minimumPurchaseConstraints.isEnabled,
    );
  }

  if (values.offerType === PromotionType.ProductDiscount) {
    return prepareProductDiscountCreateInput(values.productDiscount);
  }

  if (values.offerType === PromotionType.BuyAndGet) {
    return prepareBuyAndGetCreateInput(values.buyAndGet);
  }

  if (values.offerType === PromotionType.VarietyDiscount) {
    return prepareVarietyDiscountCreateInput(values.varietyDiscount);
  }

  if (values.offerType === PromotionType.FuelRollback) {
    return prepareFuelRollbackCreateInput(values.fuelRollback);
  }

  if (values.offerType === null) {
    throw new Error('Offer type is not selected');
  }

  throw new Error('Offer type is not supported or its configuration is not provided');
};

export const getEarningMechanismOperations = (
  offer: OfferFragment | undefined,
  values: PromotionConfigureFormValues,
  dirtyFields: DirtyFieldsType,
): Operation[] => {
  const operations: Operation[] = [];

  if (
    !offer ||
    (!isFieldDirty<PromotionConfigureFormValues>(dirtyFields, 'offerType') &&
      !isFieldDirty<PromotionConfigureFormValues>(dirtyFields, 'productDiscount') &&
      !isFieldDirty<PromotionConfigureFormValues>(dirtyFields, 'transactionDiscount') &&
      !isFieldDirty<PromotionConfigureFormValues>(dirtyFields, 'buyAndGet') &&
      !isFieldDirty<PromotionConfigureFormValues>(dirtyFields, 'varietyDiscount') &&
      !isFieldDirty<PromotionConfigureFormValues>(dirtyFields, 'fuelRollback') &&
      !isFieldDirty<PromotionConfigureFormValues>(dirtyFields, 'minimumPurchaseConstraints'))
  ) {
    return operations;
  }

  if (values.earningMechanism?.id) {
    operations.push(makeDeleteOperation('earningMechanismDelete', values.earningMechanism.id));
  }

  const configuration = getEarningMechanismCreateInput(values);
  operations.push(makeEarningMechanismCreateOperation(offer.id, configuration));

  return operations;
};
